import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { format } from 'date-fns';
import {
  Box,
  Card,
  CardHeader,
  Chip,
  CircularProgress,
  Container,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { user$ } from '@/store/userStore';
import { useUserViewMode } from '@/hooks/useUserViewMode';
import { membershipService } from '@/data/membershipService';
import {
  MembershipTransaction,
  MembershipTransactionKind
} from '@/types/MembershipTransaction.interface';
import { preparePurchaseTypeLabel } from '@/utils/helpers';
import { formatDateToDayMonthYearTime } from '@/utils/dateFormatters';
import NoDataFound from '@/components/NoDataFound';
import DateFilterBar from '@/components/filters/DateFilterBar';

const KIND_LABELS: Record<
  MembershipTransactionKind,
  { label: string; color: 'success' | 'info' | 'warning' }
> = {
  PURCHASE: { label: 'membership.transaction.kind.purchase', color: 'success' },
  EXTEND: { label: 'membership.transaction.kind.extend', color: 'info' },
  RENEW: { label: 'membership.transaction.kind.renew', color: 'warning' }
};

type MembershipTransactionsTableProps = {
  refreshKey?: number;
};

const MembershipTransactionsTable: React.FC<
  MembershipTransactionsTableProps
> = ({ refreshKey = 0 }) => {
  const { t } = useTranslation();
  const { currentBranch } = useUserViewMode();

  const [transactions, setTransactions] = useState<MembershipTransaction[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [limit, setLimit] = useState(10);
  const [dateRange, setDateRange] = useState<{
    startDate?: string;
    endDate?: string;
    timezone?: string;
  }>({});

  const branchId = currentBranch?.id;

  const fetchTransactions = useCallback(async () => {
    try {
      setLoading(true);
      const result = await membershipService.getTransactions({
        page,
        limit,
        ...(branchId && { branchId }),
        ...(dateRange.startDate && { startDate: dateRange.startDate }),
        ...(dateRange.endDate && { endDate: dateRange.endDate })
      });
      setTransactions(result.items);
      setTotalCount(result.total);
    } catch (error) {
      console.error('Error fetching membership transactions:', error);
      setTransactions([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
    // refreshKey kasıtlı bağımlılık: parent kayıt sonrası artırarak listeyi
    // yeniden çektiriyor (callback içinde doğrudan kullanılmıyor).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    page,
    limit,
    branchId,
    dateRange.startDate,
    dateRange.endDate,
    refreshKey
  ]);

  useEffect(() => {
    fetchTransactions();
  }, [fetchTransactions]);

  const handleChangePage = (_: unknown, newPage: number) => setPage(newPage);

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setLimit(parseInt(event.target.value, 10));
    setPage(0);
  };

  const customerName = (txn: MembershipTransaction) =>
    txn.user ? `${txn.user.name} ${txn.user.surname}`.trim() : '-';

  const employeeName = (txn: MembershipTransaction) =>
    txn.employee ? `${txn.employee.name} ${txn.employee.surname}`.trim() : '-';

  const dateFilterComponent = useMemo(
    () => (
      <DateFilterBar
        onChange={(range) => {
          setPage(0);
          if (range) {
            const timezone =
              user$.currentBranch.get()?.timezone ??
              Intl.DateTimeFormat().resolvedOptions().timeZone;
            setDateRange({
              startDate: format(range.startDate, 'yyyy-MM-dd'),
              endDate: format(range.endDate, 'yyyy-MM-dd'),
              timezone
            });
          } else {
            setDateRange({});
          }
        }}
        filterLabel={t('filters.date.range')}
        compact
        showClearButton
        noFilterLabel={t('filters.date.all')}
      />
    ),
    [t]
  );

  return (
    <Container disableGutters maxWidth={false} sx={{ maxWidth: '90%' }}>
      <Card>
        <CardHeader
          title={t('membership.transactions')}
          subheader={t('membership.transactions.subheader')}
          action={<Box sx={{ pt: 1 }}>{dateFilterComponent}</Box>}
        />
        <Divider />
        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" p={4}>
            <CircularProgress />
          </Box>
        ) : transactions.length === 0 ? (
          <NoDataFound message={t('membership.transactions.no.data')} />
        ) : (
          <>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>{t('date')}</TableCell>
                    <TableCell>{t('customer')}</TableCell>
                    <TableCell>{t('branch')}</TableCell>
                    <TableCell>{t('membership.plan')}</TableCell>
                    <TableCell>{t('membership.transaction.kind')}</TableCell>
                    <TableCell align="right">
                      {t('membership.transaction.duration.days')}
                    </TableCell>
                    <TableCell>{t('payment.type')}</TableCell>
                    <TableCell>
                      {t('membership.transaction.employee')}
                    </TableCell>
                    <TableCell align="right">{t('amount')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {transactions.map((txn) => {
                    const kind = KIND_LABELS[txn.kind] ?? {
                      label: txn.kind,
                      color: 'info' as const
                    };
                    return (
                      <TableRow hover key={txn.id}>
                        <TableCell>
                          <Typography variant="body2">
                            {formatDateToDayMonthYearTime(txn.createdAt)}
                          </Typography>
                        </TableCell>
                        <TableCell>{customerName(txn)}</TableCell>
                        <TableCell>{txn.branch?.name ?? '-'}</TableCell>
                        <TableCell>{txn.membershipPlan?.name ?? '-'}</TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            color={kind.color}
                            label={t(kind.label)}
                          />
                        </TableCell>
                        <TableCell align="right">{txn.durationDays}</TableCell>
                        <TableCell>
                          {t(preparePurchaseTypeLabel(txn.purchaseType))}
                        </TableCell>
                        <TableCell>{employeeName(txn)}</TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" fontWeight="bold">
                            ₺
                            {Number(txn.amount).toLocaleString('tr-TR', {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2
                            })}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
            <Divider />
            <TablePagination
              component="div"
              count={totalCount}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={limit}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={[5, 10, 25, 50]}
            />
          </>
        )}
      </Card>
    </Container>
  );
};

export default MembershipTransactionsTable;
